import React from "react";
import { Link } from "react-router-dom";
import {
  FiSearch,
  FiDatabase,
  FiUsers,
  FiCloud,
  FiMapPin,
  FiTrendingUp,
  FiArrowRight,
  FiArrowUpRight,
} from "react-icons/fi";
import WhyAbscodCard from "./WhyAbscodCard";

const differentiators = [
  {
    icon: FiSearch,
    title: "Process Before Technology",
    description:
      "We begin by understanding your existing workflows, reporting requirements, operational challenges and business priorities before configuring the solution.",
    accent: "blue",
  },
  {
    icon: FiDatabase,
    title: "Migration You Can Verify",
    description:
      "Masters, balances and transactions are migrated through a structured process supported by validation, reconciliation and parallel-run planning.",
    accent: "cyan",
  },
  {
    icon: FiUsers,
    title: "Clear Project Ownership",
    description:
      "Work with one accountable team across discovery, configuration, migration, testing, training and go-live coordination.",
    accent: "teal",
  },
  {
    icon: FiCloud,
    title: "Cloud or On-Premises",
    description:
      "Choose the deployment model that fits your infrastructure, control, accessibility and long-term business requirements.",
    accent: "indigo",
  },
  {
    icon: FiMapPin,
    title: "Built for Indian Operations",
    description:
      "Configure finance, GST, dimensions, inventory and reporting around the practical requirements of Indian businesses.",
    accent: "violet",
  },
  {
    icon: FiTrendingUp,
    title: "Adoption Beyond Go-Live",
    description:
      "Practical user training and continued functional support help teams adopt the system and improve how they use it over time.",
    accent: "magenta",
  },
];

const WhyAbscod = () => {
  return (
    <section className="relative overflow-hidden bg-[#f8f9fc] py-20 md:py-24 lg:py-28">
      {/* Dot pattern */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #475569 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
        aria-hidden="true"
      />

      {/* Ambient glows */}
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[28rem] w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/[0.04] blur-[120px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-20 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-violet-500/[0.04] blur-[110px]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="about-fade-up mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-bold tracking-[0.12em] text-slate-600 uppercase shadow-sm">
            WHY ABSCOD
          </span>

          <h2 className="mt-5 font-raleway font-extrabold leading-[1.12] tracking-tight text-slate-900 text-[clamp(1.75rem,4vw,2.75rem)]">
            Why choose Abscod for your{" "}
            <span className="text-[#FFDB16] drop-shadow-[0_0_20px_rgba(255,219,22,0.4)]">
              Business Central journey
            </span>
            ?
          </h2>

          <p className="mt-5 font-openSans text-base leading-relaxed text-slate-600 md:text-lg">
            Successful ERP transformation requires more than software. Abscod
            combines structured delivery, practical business understanding and
            accountable support to help you move forward with clarity and
            confidence.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {differentiators.map((card, index) => (
            <div
              key={card.title}
              style={{ animationDelay: `${(index + 1) * 0.05}s` }}
              className="about-fade-up h-full"
            >
              <WhyAbscodCard {...card} />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="about-fade-up about-delay-4 mt-14 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            to="/contactus"
            className="group inline-flex items-center gap-2 rounded-full bg-[#FFDB16] px-7 py-3.5 text-sm font-semibold text-slate-950 shadow-[0_12px_40px_rgba(255,219,22,0.25)] transition duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_16px_50px_rgba(255,219,22,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFDB16] focus-visible:ring-offset-2"
          >
            Discuss Your Business Central Project
            <FiArrowRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              strokeWidth={2.5}
            />
          </Link>

          <Link
            to="/software-development"
            className="group inline-flex items-center gap-2 font-openSans text-sm font-bold text-slate-700 transition duration-200 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            See Our Implementation Approach
            <FiArrowUpRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.5}
            />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WhyAbscod;
